import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useOrgId } from './useOrgId';

export const useFeatureFlag = (key: string) => {
  const { user } = useAuth();
  const { orgId } = useOrgId();

  const { data: isEnabled, isLoading } = useQuery({
    queryKey: ['feature-flag', key, user?.id, orgId],
    queryFn: async () => {
      if (!user) return false;

      if (!orgId) return false;

      // Call RPC function
      const { data, error } = await supabase.rpc('is_feature_enabled', {
        p_org_id: orgId,
        p_key: key,
      });

      if (error) {
        console.error('Error checking feature flag:', error);
        return false;
      }

      return data || false;
    },
    enabled: !!user && !!orgId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return { isEnabled: isEnabled || false, isLoading };
};

export const useFeatureFlags = (keys: string[]) => {
  const { user } = useAuth();
  const { orgId } = useOrgId();

  const { data: flags, isLoading } = useQuery({
    queryKey: ['feature-flags', keys, user?.id, orgId],
    queryFn: async () => {
      if (!user) return {};

      if (!orgId) return {};

      // Fetch all flags in one query
      const { data, error } = await supabase
        .from('feature_flags')
        .select('key, enabled')
        .eq('org_id', orgId)
        .in('key', keys);

      if (error) {
        console.error('Error fetching feature flags:', error);
        return {};
      }

      // Build map of key -> enabled
      const flagMap: Record<string, boolean> = {};
      keys.forEach((key) => {
        const flag = data?.find((f) => f.key === key);
        flagMap[key] = flag?.enabled || false;
      });

      return flagMap;
    },
    enabled: !!user && !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return { flags: flags || {}, isLoading };
};
